/**
 * Dashboard Templates
 *
 * Pre-configured dashboard layouts for different audiences and use cases.
 * Each template defines a set of widgets with their positions and sizes,
 * optimized for the target screen and user type.
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

import { WidgetInstance } from '../types';

/**
 * Template definition with metadata
 */
export interface DashboardTemplate {
  id: string;
  name: string;
  description: string;
  audience: string;
  screen: string;
  icon: string;
  color: string;
  widgets: WidgetInstance[];
  gridColumns: number;
  gridRows: number;
}

// Helper to create a widget instance
let instanceCounter = 0;
function w(
  widgetId: string,
  x: number,
  y: number,
  width: number,
  height: number,
  refreshInterval: number = 30
): WidgetInstance {
  instanceCounter++;
  return {
    instanceId: `tmpl-${instanceCounter}`,
    widgetId,
    position: { x, y },
    size: { width, height },
    settings: { refreshInterval, showHeader: true },
  };
}

/**
 * Reset instance counter (call before generating a template)
 */
function resetCounter(prefix: string): void {
  instanceCounter = 0;
}

// ================================================================
// TEMPLATE 1: Lagergulvet TV
// ================================================================
function createWarehouseFloorTV(): DashboardTemplate {
  resetCounter('floor');
  return {
    id: 'warehouse-floor-tv',
    name: 'Lagergulvet TV',
    description: 'Optimert for stor TV-skjerm p\u00e5 lagergulvet. Viser sanntidsaktivitet, plukkefremdrift og toppliste.',
    audience: 'Operat\u00f8rer',
    screen: 'TV 55\u201d+',
    icon: 'Tv',
    color: 'blue',
    gridColumns: 12,
    gridRows: 8,
    widgets: [
      w('clock', 0, 0, 2, 2, 0),
      w('animated-counter', 2, 0, 3, 2, 30),
      w('picking-stats', 5, 0, 4, 2, 30),
      w('shipping-cutoff', 9, 0, 3, 2, 30),
      w('leaderboard', 0, 2, 4, 4, 60),
      w('hourly-activity-chart', 4, 2, 5, 2, 60),
      w('goal-tracker', 9, 2, 3, 2, 30),
      w('activity-feed', 4, 4, 5, 4, 10),
      w('kpi-grid', 9, 4, 3, 2, 60),
      w('comparison', 0, 6, 4, 2, 60),
      w('mini-heatmap', 9, 6, 3, 2, 30),
    ],
  };
}

// ================================================================
// TEMPLATE 2: Lagerleder Oversikt
// ================================================================
function createManagerOverview(): DashboardTemplate {
  resetCounter('manager');
  return {
    id: 'manager-overview',
    name: 'Lagerleder Oversikt',
    description: 'Komplett oversikt for lagersjef med ordrer, plukking, shipping, operat\u00f8rer og flaskehalser.',
    audience: 'Lagersjef',
    screen: 'Desktop 27\u201d+',
    icon: 'LayoutDashboard',
    color: 'emerald',
    gridColumns: 12,
    gridRows: 10,
    widgets: [
      w('order-summary', 0, 0, 3, 2, 30),
      w('picking-stats', 3, 0, 4, 2, 30),
      w('shipping-stats', 7, 0, 3, 2, 30),
      w('clock', 10, 0, 2, 2, 0),
      w('kpi-grid', 0, 2, 6, 2, 60),
      w('operator-status', 6, 2, 3, 4, 30),
      w('bottleneck-detector', 9, 2, 3, 2, 60),
      w('hourly-activity-chart', 0, 4, 6, 2, 60),
      w('inventory-alerts', 9, 4, 3, 4, 60),
      w('throughput-time', 0, 6, 6, 2, 120),
      w('workforce-balance', 6, 6, 3, 2, 30),
      w('sla-monitor', 0, 8, 6, 2, 120),
      w('comparison', 6, 8, 3, 2, 60),
      w('replenishment-alerts', 9, 8, 3, 2, 60),
    ],
  };
}

// ================================================================
// TEMPLATE 3: Mottakssone
// ================================================================
function createReceivingZone(): DashboardTemplate {
  resetCounter('receiving');
  return {
    id: 'receiving-zone',
    name: 'Mottakssone',
    description: 'Fokus p\u00e5 mottak, innkommende leveranser og plasseringsstatus.',
    audience: 'Mottaksoperat\u00f8rer',
    screen: 'TV/Tablet',
    icon: 'Truck',
    color: 'violet',
    gridColumns: 12,
    gridRows: 8,
    widgets: [
      w('clock', 0, 0, 2, 2, 0),
      w('incoming-deliveries', 2, 0, 5, 4, 120),
      w('animated-counter', 7, 0, 3, 2, 30),
      w('replenishment-alerts', 10, 0, 2, 4, 60),
      w('sparkline-kpi', 7, 2, 3, 2, 60),
      w('activity-feed', 0, 2, 2, 4, 10),
      w('kpi-grid', 0, 6, 6, 2, 60),
      w('hourly-activity-chart', 6, 4, 6, 2, 60),
      w('goal-tracker', 2, 4, 4, 2, 30),
      w('comparison', 6, 6, 6, 2, 60),
    ],
  };
}

// ================================================================
// TEMPLATE 4: Pakke & Send
// ================================================================
function createPackAndShip(): DashboardTemplate {
  resetCounter('ship');
  return {
    id: 'pack-and-ship',
    name: 'Pakke & Send',
    description: 'Optimert for pakkestasjoner med fokus p\u00e5 shipping cut-off, klar-status og leveringer.',
    audience: 'Pakkestasjoner',
    screen: 'TV/Desktop',
    icon: 'Package',
    color: 'cyan',
    gridColumns: 12,
    gridRows: 8,
    widgets: [
      w('clock', 0, 0, 2, 2, 0),
      w('shipping-cutoff', 2, 0, 3, 2, 30),
      w('shipping-stats', 5, 0, 4, 2, 30),
      w('animated-counter', 9, 0, 3, 2, 30),
      w('order-flow', 0, 2, 4, 4, 60),
      w('kpi-grid', 4, 2, 5, 2, 60),
      w('sparkline-kpi', 9, 2, 3, 2, 60),
      w('hourly-activity-chart', 4, 4, 5, 2, 60),
      w('goal-tracker', 9, 4, 3, 2, 30),
      w('sla-monitor', 0, 6, 6, 2, 120),
      w('comparison', 6, 6, 6, 2, 60),
    ],
  };
}

// ================================================================
// TEMPLATE 5: Daglig Møte
// ================================================================
function createDailyMeeting(): DashboardTemplate {
  resetCounter('meeting');
  return {
    id: 'daily-meeting',
    name: 'Daglig M\u00f8te',
    description: 'Perfekt for daglig stand-up med gårsdagens resultater og dagens prioriteringer.',
    audience: 'Stand-up m\u00f8te',
    screen: 'Projektor',
    icon: 'Presentation',
    color: 'amber',
    gridColumns: 12,
    gridRows: 8,
    widgets: [
      w('daily-scorecard', 0, 0, 6, 3, 60),
      w('comparison', 6, 0, 6, 3, 60),
      w('leaderboard', 0, 3, 4, 5, 60),
      w('bottleneck-detector', 4, 3, 4, 3, 60),
      w('throughput-time', 8, 3, 4, 3, 120),
      w('goal-tracker', 4, 6, 4, 2, 30),
      w('workforce-balance', 8, 6, 4, 2, 30),
    ],
  };
}

// ================================================================
// TEMPLATE 6: Salgskontor
// ================================================================
function createSalesOffice(): DashboardTemplate {
  resetCounter('sales');
  return {
    id: 'sales-office',
    name: 'Salgskontor',
    description: 'For kundeservice og salg med ordrestatus, SLA og leveringspresisjon.',
    audience: 'Kundeservice',
    screen: 'Desktop',
    icon: 'Headphones',
    color: 'pink',
    gridColumns: 12,
    gridRows: 8,
    widgets: [
      w('order-summary', 0, 0, 4, 2, 30),
      w('sla-monitor', 4, 0, 5, 4, 120),
      w('sparkline-kpi', 9, 0, 3, 2, 60),
      w('order-flow', 0, 2, 4, 4, 60),
      w('pick-accuracy', 9, 2, 3, 2, 60),
      w('shipping-cutoff', 0, 6, 3, 2, 30),
      w('shipping-stats', 3, 6, 3, 2, 30),
      w('throughput-time', 6, 4, 6, 2, 120),
      w('comparison', 6, 6, 6, 2, 60),
    ],
  };
}

// ================================================================
// TEMPLATE 7: Vestibyle/Wow
// ================================================================
function createVestibuleWow(): DashboardTemplate {
  resetCounter('vestibule');
  return {
    id: 'vestibule-wow',
    name: 'Vestibylen',
    description: 'Visuelt imponerende for bes\u00f8kende med store animerte tall, heatmap og leaderboard.',
    audience: 'Bes\u00f8kende',
    screen: 'TV 65\u201d+',
    icon: 'Sparkles',
    color: 'rose',
    gridColumns: 12,
    gridRows: 8,
    widgets: [
      w('animated-counter', 0, 0, 4, 3, 30),
      w('animated-counter', 4, 0, 4, 3, 30),
      w('animated-counter', 8, 0, 4, 3, 30),
      w('mini-heatmap', 0, 3, 5, 3, 30),
      w('leaderboard', 5, 3, 4, 5, 60),
      w('hourly-activity-chart', 9, 3, 3, 3, 60),
      w('clock', 0, 6, 2, 2, 0),
      w('radial-gauge', 2, 6, 3, 2, 60),
      w('goal-tracker', 9, 6, 3, 2, 30),
    ],
  };
}

// ================================================================
// TEMPLATE 8: Executive / VD
// ================================================================
function createExecutive(): DashboardTemplate {
  resetCounter('exec');
  return {
    id: 'executive',
    name: 'VD / Executive',
    description: 'H\u00f8yniv\u00e5 KPI-er for ledelsen med OTIF, nøyaktighet, effektivitet og trender.',
    audience: 'Ledelse',
    screen: 'Desktop/iPad',
    icon: 'Crown',
    color: 'yellow',
    gridColumns: 12,
    gridRows: 8,
    widgets: [
      w('daily-scorecard', 0, 0, 6, 3, 60),
      w('sparkline-kpi', 6, 0, 3, 2, 60),
      w('pick-accuracy', 9, 0, 3, 2, 60),
      w('comparison', 6, 2, 6, 3, 60),
      w('throughput-time', 0, 3, 6, 2, 120),
      w('sla-monitor', 0, 5, 6, 3, 120),
      w('order-flow', 6, 5, 3, 3, 60),
      w('workforce-balance', 9, 5, 3, 3, 30),
    ],
  };
}

// ================================================================
// Registry
// ================================================================

/**
 * All available dashboard templates
 */
export const DASHBOARD_TEMPLATES: DashboardTemplate[] = [
  createWarehouseFloorTV(),
  createManagerOverview(),
  createReceivingZone(),
  createPackAndShip(),
  createDailyMeeting(),
  createSalesOffice(),
  createVestibuleWow(),
  createExecutive(),
];

/**
 * Get a template by its ID
 */
export function getTemplateById(id: string): DashboardTemplate | undefined {
  return DASHBOARD_TEMPLATES.find((t) => t.id === id);
}

/**
 * Create a fresh layout from a template (generates new instance IDs)
 */
export function createLayoutFromTemplate(
  templateId: string,
  layoutName?: string
): {
  id: string;
  name: string;
  widgets: WidgetInstance[];
  gridColumns: number;
  gridRows: number;
  createdAt: string;
  updatedAt: string;
  isDefault: boolean;
} | null {
  const template = getTemplateById(templateId);
  if (!template) return null;

  const now = new Date().toISOString();
  const layoutId = `layout-${Date.now()}`;

  // Generate fresh instance IDs
  const widgets = template.widgets.map((w, i) => ({
    ...w,
    instanceId: `${layoutId}-w${i + 1}`,
  }));

  return {
    id: layoutId,
    name: layoutName ?? template.name,
    widgets,
    gridColumns: template.gridColumns,
    gridRows: template.gridRows,
    createdAt: now,
    updatedAt: now,
    isDefault: false,
  };
}
